import { useAtBottom } from '@gluedigital/scrollastic';
import { useEffect, useMemo, useRef } from 'react';
import ErrorToast from 'src/components/ErrorToast/ErrorToast';
import { useStreaming } from 'src/context/StreamingContext';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { useIsBubbleWidget } from 'src/hooks/useIsBubbleWidget';
import { useIsEmbedded } from 'src/hooks/useIsEmbedded';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { Message, UserMessage as UserMessageType } from 'src/types';
import { useAppDispatch, useAppSelector } from 'universal-scripts';
import InitialQuestions from '../InitialQuestions/InitialQuestions';
import PopularQuestionsList from '../PopularQuestions/PopularQuestionsList';
import BotMessage from './from/BotMessage';
import UserMessage from './from/UserMessage';

import './Messages.sass';

declare const __SERVER__: boolean;

interface MessagesProps {
	handleEdit: {
		enable: (message: Message) => void;
		disable: () => void;
	};
	editing: Message | null;
	initialMessages?: Message[];
	streamingText: string;
	onChunk: (chunk: string) => void;
	onComplete: (fullText: string) => void;
}

const Messages = ({ handleEdit, editing, initialMessages, streamingText, onChunk, onComplete }: MessagesProps) => {
	const endRef = useRef<HTMLElement>(null);
	const dispatch = useAppDispatch();
	const { errorText, closeError } = useErrorToast();
	const storeMessages = useAppSelector((s) => s.messages);
	const messages = initialMessages ?? storeMessages;
	const isMobile = useIsMobile();
	const isBubbleWidget = useIsBubbleWidget();
	const isEmbedded = useIsEmbedded();
	const showScopeNotice = messages.length === 0 && (isMobile || isBubbleWidget || isEmbedded);

	const { streamInfo } = useStreaming();
	const { isWriting } = streamInfo;

	useEffect(() => {
		if (isWriting && endRef.current) {
			endRef.current.scrollTo({
				top: endRef.current.scrollHeight,
				behavior: 'smooth'
			});
		}
	}, [isWriting]);

	const atBottom = useAtBottom(endRef);

	const hasScrolled = !__SERVER__ && !atBottom;

	useEffect(() => {
		if (hasScrolled) {
			dispatch({ type: 'scroll/add' });
		} else {
			dispatch({ type: 'scroll/delete' });
		}
		return () => {
			dispatch({ type: 'scroll/delete' });
		};
	}, [hasScrolled, dispatch]);

	const RenderMessages = useMemo(
		() =>
			messages?.length > 0 &&
			messages.map((message, index) => {
				const isLastMessage = index === messages.length - 1;

				if (message.from !== 'user') {
					const messageText = isLastMessage && !message.completed ? (message.text || '') + streamingText : message.text;

					return (
						<BotMessage
							key={message.id || `bot-${index}`}
							message={{ ...message, text: messageText }}
							replyTo={messages[index - 1] as UserMessageType}
						/>
					);
				} else {
					return <UserMessage key={message.id || `user-${index}`} message={message} handleEdit={handleEdit} editing={editing} />;
				}
			}),
		[messages, editing, handleEdit, streamingText]
	);

	return (
		<section ref={endRef} id="chat-messages">
			<div className="chat-wrapper">
				<InitialQuestions showHeader={!initialMessages} showScopeNotice={showScopeNotice}>
					{messages?.length === 0 && <PopularQuestionsList onChunk={onChunk} onComplete={onComplete} />}
				</InitialQuestions>
				{RenderMessages}
			</div>
			<ErrorToast errorText={errorText} closeError={closeError} />
		</section>
	);
};

export default Messages;
